import { ServerResponse } from 'http';
import Router from 'next/router';
import * as Sentry from '@sentry/nextjs';

import { FeatureNames } from '@/core/interfaces/store';
import {
  AutoCompleteAddress,
  ContactInfo,
  CustomFieldsForm,
  CustomFieldWithValue,
  extendPrefixTypes,
  Nullable,
  prefixTypes,
  QueryParams,
  TakeActionErrorsFields,
  TakeActionFormState,
} from '@/core/interfaces/common';

import {
  fieldTypes,
  prefixes,
} from '@/utils/constants';

export const isBrowser = !!(typeof window !== 'undefined' && window.document && window.document.createElement);

export const redirect = (res: ServerResponse | undefined, route: string) => {
  if (!isBrowser && res) {
    res.writeHead(307, {
      Location: route,
    });
    res.end();
  } else {
    Router.replace(route);
  }
};

export const getActionPrefix = (featureName: FeatureNames) => featureName.toUpperCase();

export const formatPhoneNumber = (phoneNumberString: string): Nullable<string> => {
  if (phoneNumberString) {
    const match = phoneNumberString.match(/^(\d{3})(\d{3})(\d{4})$/);

    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }

    return phoneNumberString;
  }

  return null;
};

export const replaceStringLineBreaksWithHtml = (value: string) => {
  if (!value?.replace) {
    return '';
  }

  return value.replace(/(?:\r\n|\r|\n)/g, '<br />');
};

export const handleNumberFormat = (value: string) => {
  // Strip everything except digits, then remove a leading country code "1"
  // (handles +1, 1-, spaces, parentheses from autocomplete/paste)
  const digits = value.replace(/\D/g, '').replace(/^1(?=\d{10})/, '')
    .slice(0, 10);

  if (digits.length < 10) {
    return digits;
  }

  return formatPhoneNumber(digits);
};

export const hexToRgb = (hex: string, alpha: string) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  if (alpha) {
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  return `rgb(${r}, ${g}, ${b})`;
};

export const getFormattedAddress = (option: string | AutoCompleteAddress, isForInput = false) => {
  if (typeof option === 'string') {
    return option;
  }

  let whiteSpace = '';
  let secondary = '';

  if (option.secondary) {
    secondary = option.secondary;

    if (option.entries > 1) {
      secondary += ` (${option.entries} entries)`;
    }
    whiteSpace = ' ';
  }

  if (isForInput) {
    return `${option.streetLine}${whiteSpace}${secondary}`;
  }

  return `${option.streetLine}${whiteSpace}${secondary} ${option.city}, ${option.state} ${option.zipcode}`;
};

export const findLongestCommonSubstring = (input: string, wholeString: string) => {
  for (let i = input.length; i > 0; i -= 1) {
    const reverseInput = Array.from(input.substr(0, i))
      .reverse()
      .join('');
    const reverseWholeString = Array.from(wholeString)
      .reverse()
      .join('');

    const reverseIndex = reverseWholeString.toLowerCase().indexOf(reverseInput.toLowerCase());
    const splitIndex = reverseIndex > 0 ? reverseWholeString.length - reverseIndex : -1;
    const commonPart = wholeString.substr(0, splitIndex);
    const restOfTheString = wholeString.substr(splitIndex);

    if (splitIndex >= 0) {
      return [
        commonPart,
        restOfTheString,
      ];
    }
  }

  return [
    '',
    wholeString,
  ];
};

export const scrollToTop = () => window?.scrollTo({
  behavior: 'smooth',
  top: 0,
});

export const hasAnyError = (error: TakeActionErrorsFields) => Object.entries(error).find(([
  key,
  value,
]) => {
  if (key === 'customFields') {
    return value?.find((customField: { value: [string] }) => {
      if (customField?.value && Array.isArray(customField.value)) {
        if (typeof customField?.value[0] === 'string') {
          return !!value[0];
        }
      }

      return false;
    });
  }

  if (key === 'messages') {
    return value?.some((message: {
      message: [string];
      subject: [string];
    }) => {
      if (message?.message && Array.isArray(message.message)) {
        if (typeof message?.message[0] === 'string') {
          return !!value[0];
        }
      }

      if (message?.subject && Array.isArray(message.subject)) {
        if (typeof message?.subject[0] === 'string') {
          return !!value[0];
        }
      }

      return false;
    });
  }

  if (typeof value === 'string') {
    return !!value;
  }

  if (Array.isArray(value)) {
    if (typeof value[0] === 'string') {
      return !!value[0];
    }
  }

  return false;
});

const normalizeIdentity = (value?: string) => (value || '').trim().toLowerCase();

/**
 * The prefilled contact's uuid may only be submitted while the person
 * submitting is still that contact. Resetting the form, or overwriting the
 * identity fields (email, first name, last name) by hand, means the
 * submission is for someone else and must not update the prefilled contact.
 */
export const getPrefilledContactUuid = (
  contactInfo: Nullable<ContactInfo> | undefined,
  submitted: { email?: string; firstName?: string; lastName?: string },
  isNewContact: boolean
): { uuid?: string } => {
  if (isNewContact || !contactInfo?.uuid) {
    return {};
  }

  const isSameIdentity = (
    normalizeIdentity(submitted.email) === normalizeIdentity(contactInfo.email) &&
    normalizeIdentity(submitted.firstName) === normalizeIdentity(contactInfo.firstName) &&
    normalizeIdentity(submitted.lastName) === normalizeIdentity(contactInfo.lastName)
  );

  return isSameIdentity ? { uuid: contactInfo.uuid } : {};
};

export const transformPrefix = (prefix: extendPrefixTypes) => {
  if (!prefix) {
    return '';
  }

  if (prefix.endsWith('.')) {
    return prefix as prefixTypes;
  }

  return `${prefix}.` as prefixTypes;
};

export const handleCreateCustomFieldsParams = (
  customFields: Array<CustomFieldWithValue>
): Array<CustomFieldsForm> => customFields.map(({
  fieldType,
  uuid: customFieldUuid,
  value,
  values,
}) => ({
  uuid: customFieldUuid,
  ...(fieldType === fieldTypes.multipleChoiceField ?
    { values: values?.map(({ value: option }) => option) } :
    { value }),
}));

export const isValidUrl = (urlString: string | undefined) => {
  if (!urlString) {
    return false;
  }

  const urlRegex = /^(https?:\/\/)?([a-zA-Z0-9.-]+\.[a-zA-Z]{2,})(:[0-9]+)?(\/[^\s]*)?$/;
  const isValid = urlRegex.test(urlString);

  if (!isValid) {
    Sentry.captureException(new Error('Url from REACT_APP_ACTION_CENTER_ROOT is not a valid url'));
  }

  return isValid;
};

/*
 Query parameters a customer can use to prefill the landing page form,
 keyed by the form state field each one populates.
 */
export const prefillQueryParams = {
  email: 'mus_email',
  firstName: 'mus_fname',
  lastName: 'mus_lname',
  phoneNumber: 'mus_phone',
  prefix: 'mus_prefix',
  streetAddress: 'mus_street',
  zipCode: 'mus_zip',
} as const;

export type ContactField = keyof typeof prefillQueryParams;

type PrefillFormState = {
  [key in ContactField]?: string | number;
};

// The API's contact field limits. Anything arriving from outside (a posted
// form, a decrypted prefill token) is capped to these.
export const maxContactFieldLengths: { [field in ContactField]: number } = {
  email: 254,
  firstName: 250,
  lastName: 250,
  phoneNumber: 30,
  prefix: 50,
  streetAddress: 250,
  zipCode: 10,
};

export const capContactField = (field: ContactField, value: string) => (
  value.trim().slice(0, maxContactFieldLengths[field])
);

/*
 The known prefix matching a value, ignoring case and a trailing dot
 ('dr', 'DR.' and 'Dr.' all give 'Dr.'), or '' for an unknown one.
 */
export const matchPrefix = (value: string): prefixTypes => {
  const normalize = (prefix: string) => prefix
    .trim()
    .toLowerCase()
    .replace(/\.$/, '');
  const normalized = normalize(value);

  return (prefixes.find(prefix => normalize(prefix) === normalized) || '') as prefixTypes;
};

/*
 Builds the mus_* params read by updateFormStateFromQueryParams, so a
 landing page arrives with the form mostly filled out. Empty values are
 omitted.
 */
export const buildPrefillParams = (formState: PrefillFormState): QueryParams => {
  const params: QueryParams = {};

  (Object.keys(prefillQueryParams) as Array<keyof typeof prefillQueryParams>).forEach(field => {
    const value = formState[field];

    if (value) {
      params[prefillQueryParams[field]] = String(value);
    }
  });

  return params;
};

/*
 Keeps only known mus_* params with string values, capped in length, e.g.
 from a decrypted mus_data payload (which anyone can make). Returns null
 when nothing usable remains.
 */
export const sanitizePrefillParams = (value: unknown): QueryParams | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  const source = value as { [key: string]: unknown };
  const params: QueryParams = {};

  (Object.keys(prefillQueryParams) as Array<ContactField>).forEach(field => {
    const param = prefillQueryParams[field];
    const paramValue = source[param];

    if (typeof paramValue === 'string' && paramValue.trim()) {
      params[param] = capContactField(field, paramValue);
    }
  });

  return Object.keys(params).length ? params : null;
};

/*
 Populates the form based on data provided in query parameters.
 This allows a customer to populate forms from their own email
 system.
 */
export const updateFormStateFromQueryParams = (
  queryParams: QueryParams,
  formState: TakeActionFormState
): TakeActionFormState | null => {
  const updatedFormState = { ...formState };
  let hasUpdates = false;

  const formFields = Object.values(prefillQueryParams);

  formFields.forEach(field => {
    const value = queryParams[field];

    if (value && typeof value === 'string') {
      switch (field) {
        case 'mus_fname':
          updatedFormState.firstName = value;
          break;
        case 'mus_lname':
          updatedFormState.lastName = value;
          break;
        case 'mus_email':
          updatedFormState.email = value;
          break;
        case 'mus_phone':
          updatedFormState.phoneNumber = handleNumberFormat(value) || '';
          break;
        case 'mus_street':
          updatedFormState.streetAddress = value;
          break;
        case 'mus_zip':
          updatedFormState.zipCode = value;
          break;
        case 'mus_prefix': {
          const prefix = matchPrefix(value);

          if (prefix) {
            updatedFormState.prefix = prefix;
          }
          break;
        }
        default:
          break;
      }
      hasUpdates = true;
    }
  });

  return hasUpdates ? updatedFormState : null;
};
